import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { useTranslate } from 'hooks/useTranslate';
import {
  ChangeEvent,
  FC,
  ReactNode,
  createContext,
  useContext,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';
import cleanText from 'utils/cleanText';
import { getNewOperationDate } from 'utils/getNewOperationDate';

import operationTypes from 'constants/operationTypes';
import fileTypes from 'constants/fileTypes';
import documentTypes from 'constants/documentTypes';

import { SelectorOption } from 'types';

import { setModal } from 'modules/_shared/redux/modalActions';
import { addDocument } from 'modules/documents/redux/documentActions';
import { addOperation } from 'modules/transactions/redux/operationActions';
import { useGetPartnerOptionsForOperationModal } from 'modules/transactions/hooks/useGetPartnerOptionsForOperationModal';
import { useGetLastOperationDate } from 'modules/transactions/hooks/useGetLastOperationDate';

import { InformationalModalContextType } from '../types/InformationalModalContextType';
import { informationalModalInitValue } from '../constants/informationalModalInitValue';

const InformationalModalContext =
  createContext<InformationalModalContextType>(informationalModalInitValue);

export const InformationalModalProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const user = useSelector((state: State) => state.user);

  const [alias, setAlias] = useState<string>('OP_ALIAS_CANJE');
  const [date, setDate] = useState<string>('');
  const [comments, setComments] = useState<string>('');
  const [file, setFile] = useState<File | undefined>();
  const [selectedParticipants, setSelectedParticipants] = useState<
    SelectorOption[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { lastOperationDate, formattedLastOperationDate } =
    useGetLastOperationDate(actualSociety?._id).data;

  const { data: partnerOptionsData } = useGetPartnerOptionsForOperationModal({
    societyId: actualSociety?.['_id'],
  });

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  const handleChangeAlias = (value: string) => {
    setAlias(value);
  };

  const handleChangeDate = (event: ChangeEvent<HTMLInputElement>) => {
    setDate(event.target.value);
  };

  const handleChangeComments = (comment: string) => {
    setComments(comment);
  };

  const handleChangeFile = (selectedFile: File) => {
    setFile(selectedFile);
  };

  const handleChangeParticipants = (options: SelectorOption[]) => {
    setSelectedParticipants(options || []);
  };

  const handleSaveInformational = async () => {
    try {
      setIsLoading(true);

      let uploadedDocument = null;

      if (file) {
        const newDocument = {
          file,
          date: new Date(date),
          size: file.size,
          name: operationTypes.INFORMATIONAL_FILENAME,
          author: user['_id'],
          society: actualSociety['_id'],
          fileType: fileTypes[file.type as keyof typeof fileTypes],
          category: documentTypes.SCRIPTURES,
          description: t('DocumentLinkedToOperation'),
        };
        uploadedDocument = await store.dispatch(addDocument(newDocument));
      }

      const newOperationDate = getNewOperationDate(lastOperationDate, date);

      const operationData = {
        operationType: operationTypes.INFORMATIONAL,
        alias: cleanText(alias) || 'OP_ALIAS_CANJE',
        date: newOperationDate,
        society: actualSociety['_id'],
        user: user['_id'],
        comments: cleanText(comments),
        nominalValue: actualSociety.nominalValue,
        participants: selectedParticipants.map((option) => option.value),
        documents: uploadedDocument?.['_id'] ? [uploadedDocument['_id']] : [],
      };

      await store.dispatch(addOperation(operationData));

      successSwal(t('OperationSavedSuccessfully'));
    } catch (error) {
      errorSwal(t('ErrorAddingInformationalOperation'));
    } finally {
      setIsLoading(false);
      handleCloseModal();
    }
  };

  return (
    <InformationalModalContext.Provider
      value={{
        data: {
          alias,
          date,
          comments,
          file,
          lastOperationDate: formattedLastOperationDate,
          selectedParticipants,
          participantOptions: partnerOptionsData.partnerFirstOptions,
          isLoading,
        },
        actions: {
          handleCloseModal,
          handleChangeAlias,
          handleChangeDate,
          handleChangeComments,
          handleChangeFile,
          handleChangeParticipants,
          handleSaveInformational,
        },
      }}
    >
      {children}
    </InformationalModalContext.Provider>
  );
};

export const useInformationalModal = () => {
  const context = useContext(InformationalModalContext);
  if (!context) {
    throw new Error(
      'useInformationalModal must be used within an InformationalModalProvider'
    );
  }
  return context;
};
